import { useState, useCallback } from 'react'
import { ContentMetadataText, hasMetadataMath } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { ChevronDown, ChevronRight, RotateCcw, Save, Loader2 } from 'lucide-react'
import type { PrintBookLessonSettings } from '@/api/printBook'
import type { QuizScript } from '@quebon/shared-learning'
import { toQuizData } from '@quebon/shared-learning'

interface Props {
  stepQuizzes: QuizScript[][]
  settings: PrintBookLessonSettings | null
  isDirty: boolean
  isSaving: boolean
  selectedQuiz: { stepIdx: number; quizIdx: number } | null
  /** 레슨 제목 원본 (override가 비어있을 때 표시) */
  originalTitle: string
  onSettingsChange: (settings: PrintBookLessonSettings | null) => void
  onSave: () => void
  onReset: () => void
  onSelectQuiz: (stepIdx: number, quizIdx: number) => void
}

/** 최대 2줄까지만 허용 (3줄 이상 입력 시 처음 2줄만 유지) */
function clampTwoLines(value: string): string {
  const lines = value.split('\n')
  if (lines.length <= 2) return value
  return lines.slice(0, 2).join('\n')
}

const STEP_LABELS = ['S (선택)', 'B (블럭)', 'A (적용)']
const DEFAULT_SCALE = 1.0
const DEFAULT_STEP_SPACINGS: Record<number, number> = {
  0: 48,
  1: 10,
  2: 48,
}
const DEFAULT_SPACING = 48
const DEFAULT_ADDITIONAL_SCALE = 1.0
const DEFAULT_OPTION_SCALE = 1.0
const SCALE_MIN = 0.1
const SCALE_MAX = 2.0
const OPTION_SCALE_MAX = 3.0
const SCALE_STEP = 0.05
const SPACING_MIN = 0
const SPACING_MAX = 200

function getQuizScale(
  settings: PrintBookLessonSettings | null,
  stepIdx: number,
  quizIdx: number
): number {
  return settings?.steps?.[String(stepIdx)]?.quizzes?.[String(quizIdx)]?.scale
    ?? settings?.defaultScale
    ?? DEFAULT_SCALE
}

function getQuizSpacing(
  settings: PrintBookLessonSettings | null,
  stepIdx: number,
  quizIdx: number
): number {
  return settings?.steps?.[String(stepIdx)]?.quizzes?.[String(quizIdx)]?.spacing
    ?? settings?.defaultStepSpacings?.[String(stepIdx)]
    ?? DEFAULT_STEP_SPACINGS[stepIdx]
    ?? DEFAULT_SPACING
}

function getDefaultStepSpacing(
  settings: PrintBookLessonSettings | null,
  stepIdx: number,
): number {
  return settings?.defaultStepSpacings?.[String(stepIdx)]
    ?? DEFAULT_STEP_SPACINGS[stepIdx]
    ?? DEFAULT_SPACING
}

function getQuizAdditionalScale(
  settings: PrintBookLessonSettings | null,
  stepIdx: number,
  quizIdx: number,
  imageIdx: number,
): number {
  const perImg = settings?.steps?.[String(stepIdx)]?.quizzes?.[String(quizIdx)]
    ?.additionalScales?.[String(imageIdx)]
  if (perImg != null) return perImg
  return settings?.defaultAdditionalScale ?? DEFAULT_ADDITIONAL_SCALE
}

function getQuizOptionScale(
  settings: PrintBookLessonSettings | null,
  stepIdx: number,
  quizIdx: number,
): number {
  return settings?.steps?.[String(stepIdx)]?.quizzes?.[String(quizIdx)]?.optionScale
    ?? settings?.defaultOptionScale
    ?? DEFAULT_OPTION_SCALE
}

function getQuizAdditionalScalesMap(
  settings: PrintBookLessonSettings | null,
  stepIdx: number,
  quizIdx: number,
  additionalImagesLength: number,
): Record<number, number> {
  const map: Record<number, number> = {}
  for (let i = 0; i < additionalImagesLength; i++) {
    map[i] = getQuizAdditionalScale(settings, stepIdx, quizIdx, i)
  }
  return map
}

export {
  getQuizScale,
  getQuizSpacing,
  getQuizAdditionalScale,
  getQuizOptionScale,
  getQuizAdditionalScalesMap,
}

export default function LessonPreviewSettingsPanel({
  stepQuizzes,
  settings,
  isDirty,
  isSaving,
  selectedQuiz,
  originalTitle,
  onSettingsChange,
  onSave,
  onReset,
  onSelectQuiz,
}: Props) {
  const [expandedQuizzes, setExpandedQuizzes] = useState<Set<string>>(new Set())

  const toggleQuiz = useCallback((key: string, stepIdx: number, quizIdx: number) => {
    setExpandedQuizzes(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
    onSelectQuiz(stepIdx, quizIdx)
  }, [onSelectQuiz])

  const ensureSettings = useCallback((): PrintBookLessonSettings => {
    return settings ?? { defaultScale: DEFAULT_SCALE, steps: {} }
  }, [settings])

  const updateDefaultScale = useCallback((value: number) => {
    const s = ensureSettings()
    onSettingsChange({ ...s, defaultScale: value })
  }, [ensureSettings, onSettingsChange])

  const updateDefaultStepSpacing = useCallback((stepIdx: number, value: number) => {
    const s = ensureSettings()
    onSettingsChange({
      ...s,
      defaultStepSpacings: {
        ...(s.defaultStepSpacings ?? {}),
        [String(stepIdx)]: value,
      },
    })
  }, [ensureSettings, onSettingsChange])

  const updateDefaultAdditionalScale = useCallback((value: number) => {
    const s = ensureSettings()
    onSettingsChange({ ...s, defaultAdditionalScale: value })
  }, [ensureSettings, onSettingsChange])

  const updateDefaultOptionScale = useCallback((value: number) => {
    const s = ensureSettings()
    onSettingsChange({ ...s, defaultOptionScale: value })
  }, [ensureSettings, onSettingsChange])

  const updateTitleOverride = useCallback((value: string, original: string) => {
    const s = ensureSettings()
    const next = { ...s }
    const clamped = clampTwoLines(value)
    // 빈 값이거나 원본과 동일하면 override 제거 → 원본 사용
    if (clamped.trim().length === 0 || clamped === original) {
      delete next.titleOverride
    } else {
      next.titleOverride = clamped
    }
    onSettingsChange(next)
  }, [ensureSettings, onSettingsChange])

  type QuizSettingUpdate =
    | { field: 'scale' | 'spacing' | 'optionScale'; value: number | undefined }
    | { field: 'additionalScales'; imageIdx: number; value: number | undefined }

  const updateQuizSetting = useCallback((
    stepIdx: number,
    quizIdx: number,
    update: QuizSettingUpdate,
  ) => {
    const s = ensureSettings()
    const stepKey = String(stepIdx)
    const quizKey = String(quizIdx)
    const steps = { ...s.steps }
    if (!steps[stepKey]) steps[stepKey] = { quizzes: {} }
    const quizzes = { ...steps[stepKey].quizzes }
    const current = quizzes[quizKey] ? { ...quizzes[quizKey] } : {}

    if (update.field === 'additionalScales') {
      const map = { ...(current.additionalScales ?? {}) }
      if (update.value === undefined) {
        delete map[String(update.imageIdx)]
      } else {
        map[String(update.imageIdx)] = update.value
      }
      if (Object.keys(map).length > 0) current.additionalScales = map
      else delete current.additionalScales
    } else {
      if (update.value === undefined) delete (current as Record<string, unknown>)[update.field]
      else (current as Record<string, unknown>)[update.field] = update.value
    }

    if (Object.keys(current).length > 0) {
      quizzes[quizKey] = current
    } else {
      delete quizzes[quizKey]
    }
    steps[stepKey] = { quizzes }
    onSettingsChange({ ...s, steps })
  }, [ensureSettings, onSettingsChange])

  const defaultScale = settings?.defaultScale ?? DEFAULT_SCALE
  const defaultStepSpacings = STEP_LABELS.map((_, stepIdx) => getDefaultStepSpacing(settings, stepIdx))
  const defaultAdditionalScale = settings?.defaultAdditionalScale ?? DEFAULT_ADDITIONAL_SCALE
  const defaultOptionScale = settings?.defaultOptionScale ?? DEFAULT_OPTION_SCALE

  return (
    <div className="w-[280px] shrink-0 border-l border-slate-200 bg-white flex flex-col sticky top-12 max-h-[calc(100vh-3rem)] print:hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-700">프리뷰 설정</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* 레슨 제목 override */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-xs font-semibold text-slate-500 mb-2">레슨 제목</div>
          <label className="block">
            <span className="text-xs text-slate-600">
              제목 (Enter로 줄바꿈, 최대 2줄)
            </span>
            <textarea
              value={settings?.titleOverride ?? originalTitle}
              onChange={e => updateTitleOverride(e.target.value, originalTitle)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const current = (e.currentTarget.value ?? '')
                  // 이미 1개 줄바꿈 있으면 추가 Enter 차단
                  if (current.split('\n').length >= 2) {
                    e.preventDefault()
                  }
                }
              }}
              rows={2}
              className="mt-1 w-full px-2 py-1.5 text-xs border border-slate-300 rounded resize-none focus:outline-none focus:ring-1 focus:ring-blue-400 font-sans"
            />
          </label>
          {hasMetadataMath(settings?.titleOverride ?? originalTitle) && (
            <p className="mt-2 text-xs text-foreground" data-testid="lesson-title-preview">
              <ContentMetadataText text={settings?.titleOverride ?? originalTitle} />
            </p>
          )}
          <p className="mt-1 text-[10px] text-slate-400 leading-tight">
            원본 제목이 미리 입력되어 있습니다. 모두 지우면 원본으로 복원됩니다.
          </p>
        </div>

        {/* 레슨 기본값 */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-xs font-semibold text-slate-500 mb-3">레슨 기본값</div>

          <label className="block mb-3">
            <span className="text-xs text-slate-600">기본 스케일 (문제 이미지)</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="range"
                min={SCALE_MIN}
                max={SCALE_MAX}
                step={SCALE_STEP}
                value={defaultScale}
                onChange={e => updateDefaultScale(Number(e.target.value))}
                className="flex-1 h-1.5 accent-blue-500"
              />
              <span className="text-xs text-slate-700 w-10 text-right tabular-nums">
                {defaultScale.toFixed(2)}
              </span>
            </div>
          </label>

          <label className="block mb-3">
            <span className="text-xs text-slate-600">기본 추가 이미지 스케일</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="range"
                min={SCALE_MIN}
                max={SCALE_MAX}
                step={SCALE_STEP}
                value={defaultAdditionalScale}
                onChange={e => updateDefaultAdditionalScale(Number(e.target.value))}
                className="flex-1 h-1.5 accent-blue-500"
              />
              <span className="text-xs text-slate-700 w-10 text-right tabular-nums">
                {defaultAdditionalScale.toFixed(2)}
              </span>
            </div>
          </label>

          <label className="block mb-3">
            <span className="text-xs text-slate-600">기본 보기 이미지 스케일</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="range"
                min={SCALE_MIN}
                max={OPTION_SCALE_MAX}
                step={SCALE_STEP}
                value={defaultOptionScale}
                onChange={e => updateDefaultOptionScale(Number(e.target.value))}
                className="flex-1 h-1.5 accent-blue-500"
              />
              <span className="text-xs text-slate-700 w-10 text-right tabular-nums">
                {defaultOptionScale.toFixed(2)}
              </span>
            </div>
          </label>

          <div className="space-y-2">
            {STEP_LABELS.map((label, stepIdx) => (
              <label key={stepIdx} className="block">
                <span className="text-xs text-slate-600">
                  Step {stepIdx + 1} 기본 간격 ({label})
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    min={SPACING_MIN}
                    max={SPACING_MAX}
                    value={defaultStepSpacings[stepIdx]}
                    onChange={e => updateDefaultStepSpacing(stepIdx, Number(e.target.value))}
                    className="w-20 px-2 py-1 text-xs border border-slate-300 rounded"
                  />
                  <span className="text-xs text-slate-400">px</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Step별 퀴즈 목록 */}
        {stepQuizzes.map((quizzes, stepIdx) => {
          if (quizzes.length === 0) return null
          return (
            <div key={stepIdx} className="border-b border-slate-100">
              <div className="px-4 py-2 bg-slate-50">
                <span className="text-xs font-semibold text-slate-500">
                  Step {stepIdx + 1} ({STEP_LABELS[stepIdx]}) · {quizzes.length}문제
                </span>
              </div>
              {quizzes.map((script, quizIdx) => {
                const key = `${stepIdx}-${quizIdx}`
                const isExpanded = expandedQuizzes.has(key)
                const isSelected = selectedQuiz?.stepIdx === stepIdx && selectedQuiz?.quizIdx === quizIdx
                const quizScale = getQuizScale(settings, stepIdx, quizIdx)
                const quizSpacing = getQuizSpacing(settings, stepIdx, quizIdx)
                const quizOptionScale = getQuizOptionScale(settings, stepIdx, quizIdx)
                const additionalCount = toQuizData(script).additionalImages?.length ?? 0
                const hasOptionImages =
                  script.choices?.some((c) => c.image != null) ||
                  script.pairs?.left?.some((p) => p.image != null) ||
                  script.pairs?.right?.some((p) => p.image != null)
                const hasOverride = settings?.steps?.[String(stepIdx)]?.quizzes?.[String(quizIdx)] != null

                return (
                  <div key={key}>
                    <button
                      onClick={() => toggleQuiz(key, stepIdx, quizIdx)}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-slate-50 transition-colors ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                    >
                      {isExpanded
                        ? <ChevronDown size={14} className="text-slate-400 shrink-0" />
                        : <ChevronRight size={14} className="text-slate-400 shrink-0" />
                      }
                      <span className="text-xs text-slate-700 flex-1">
                        문제 {quizIdx + 1}
                      </span>
                      {hasOverride && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="px-6 pb-3 space-y-2">
                        <label className="block">
                          <span className="text-xs text-slate-500">스케일 (문제 이미지)</span>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="range"
                              min={SCALE_MIN}
                              max={SCALE_MAX}
                              step={SCALE_STEP}
                              value={quizScale}
                              onChange={e => updateQuizSetting(stepIdx, quizIdx, { field: 'scale', value: Number(e.target.value) })}
                              className="flex-1 h-1.5 accent-blue-500"
                            />
                            <span className="text-xs text-slate-700 w-10 text-right tabular-nums">
                              {quizScale.toFixed(2)}
                            </span>
                          </div>
                        </label>

                        {hasOptionImages && (
                          <label className="block">
                            <span className="text-xs text-slate-500">보기 이미지 스케일</span>
                            <div className="flex items-center gap-2 mt-1">
                              <input
                                type="range"
                                min={SCALE_MIN}
                                max={OPTION_SCALE_MAX}
                                step={SCALE_STEP}
                                value={quizOptionScale}
                                onChange={e => updateQuizSetting(stepIdx, quizIdx, { field: 'optionScale', value: Number(e.target.value) })}
                                className="flex-1 h-1.5 accent-blue-500"
                              />
                              <span className="text-xs text-slate-700 w-10 text-right tabular-nums">
                                {quizOptionScale.toFixed(2)}
                              </span>
                            </div>
                          </label>
                        )}

                        {Array.from({ length: additionalCount }).map((_, imageIdx) => {
                          const scaleVal = getQuizAdditionalScale(settings, stepIdx, quizIdx, imageIdx)
                          return (
                            <label key={`addl-${imageIdx}`} className="block">
                              <span className="text-xs text-slate-500">추가 이미지 #{imageIdx + 1} 스케일</span>
                              <div className="flex items-center gap-2 mt-1">
                                <input
                                  type="range"
                                  min={SCALE_MIN}
                                  max={SCALE_MAX}
                                  step={SCALE_STEP}
                                  value={scaleVal}
                                  onChange={e => updateQuizSetting(stepIdx, quizIdx, { field: 'additionalScales', imageIdx, value: Number(e.target.value) })}
                                  className="flex-1 h-1.5 accent-blue-500"
                                />
                                <span className="text-xs text-slate-700 w-10 text-right tabular-nums">
                                  {scaleVal.toFixed(2)}
                                </span>
                              </div>
                            </label>
                          )
                        })}

                        <label className="block">
                          <span className="text-xs text-slate-500">간격</span>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="number"
                              min={SPACING_MIN}
                              max={SPACING_MAX}
                              value={quizSpacing}
                              onChange={e => updateQuizSetting(stepIdx, quizIdx, { field: 'spacing', value: Number(e.target.value) })}
                              className="w-20 px-2 py-1 text-xs border border-slate-300 rounded"
                            />
                            <span className="text-xs text-slate-400">px</span>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      {/* 하단 버튼 */}
      <div className="px-4 py-3 border-t border-slate-200 flex gap-2">
        <button
          onClick={onReset}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
        >
          <RotateCcw size={12} /> 초기화
        </button>
        <button
          onClick={onSave}
          disabled={!isDirty || isSaving}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving
            ? <><Loader2 size={12} className="animate-spin" /> 저장 중</>
            : <><Save size={12} /> 저장</>
          }
        </button>
      </div>
    </div>
  )
}
